import { Alert, Checkbox, FormControlLabel, FormGroup } from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getAgreementIdForRequest } from '@omnea-app/agreements/agreementsFrontendClient'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { invalidateRecordAuthorization } from '@omnea-app/fe-authz/recordAuthorizationQuery'
import { ADMINISTER_AGREEMENT, ADMINISTER_SUPPLIER } from '@omnea-app/fe-authz/recordRules'
import { useIsPermitted } from '@omnea-app/fe-authz/useIsPermitted'
import { updateRecordConfidentiality } from '@omnea-app/fe-authz/useUpdateRecordConfidentiality'
import { GetRequestDocumentsQueryKey } from '@omnea-app/fe-documents/useGetRequestDocuments'
import { ConfirmDialog } from '@omnea-app/omui/ConfirmDialog'
import { isDefined } from '@omnea-app/packages/universal/type-guards/isDefined'
import { RequestDetailQueryKey, useRequestDetail } from '../../../../useRequestDetail'
import { markConfidentialInOrder } from './markConfidentialInOrder'

type Parent = { entity: 'supplier' | 'agreement'; id: string; name: string }

type MarkTarget = { entity: 'request' | Parent['entity']; id: string; name: string }

const ADMINISTER_RULES = { supplier: ADMINISTER_SUPPLIER, agreement: ADMINISTER_AGREEMENT }

const AlsoMarkCheckbox = ({
  parent,
  checked,
  disabled,
  onChange,
}: {
  parent: Parent
  checked: boolean
  disabled: boolean
  onChange: (checked: boolean) => void
}) => {
  const { t } = useTranslation('requests')
  const canAdminister = useIsPermitted(ADMINISTER_RULES[parent.entity], parent.id)
  // The label names the parent, which the caller may not be allowed to see, so a parent they cannot mark is left out.
  if (!canAdminister) return null
  return (
    <FormControlLabel
      control={<Checkbox checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />}
      label={t(`confidentiality.also-mark-${parent.entity}`, { name: parent.name })}
    />
  )
}

type Props = {
  requestId: string
  bodyTexts: string[]
  onClose: () => void
}

/**
 * Marks a request confidential, and optionally its supplier and agreement. Each mark is its own call, the request's
 * first: one that fails leaves the others in place, and the user is told which records were and were not marked.
 */
export const MarkRequestConfidentialDialog = ({ requestId, bodyTexts, onClose }: Props) => {
  const { t } = useTranslation('requests')
  const { getToken } = useAuthenticatedUser()
  const queryClient = useQueryClient()
  const { enqueueSnackbar } = useSnackbar()
  const { data: request } = useRequestDetail(requestId)
  const [tickedIds, setTickedIds] = useState<ReadonlySet<string>>(new Set())
  const [isMarking, setIsMarking] = useState(false)

  // A parent already in `inheritedFrom` is confidential already, so marking it would change nothing.
  const agreement = request && getAgreementIdForRequest({ agreement_term: request.agreementTerm, sourceRenewal: request.renewal })
  // The agreement is marked before the supplier: the supplier's mark converges its agreements at once, which can take
  // away the caller's view of the agreement before its own mark is made.
  const parents: Parent[] = [
    agreement && { entity: 'agreement' as const, id: agreement.id, name: agreement.name },
    request?.supplier && { entity: 'supplier' as const, id: request.supplier.id, name: request.supplier.name },
  ]
    .filter(isDefined)
    .filter((parent) => !request?.inheritedFrom.some(({ type, id }) => type === parent.entity && id === parent.id))

  const setTicked = (id: string, checked: boolean) => {
    const next = new Set(tickedIds)
    if (checked) next.add(id)
    else next.delete(id)
    setTickedIds(next)
  }

  const mark = async () => {
    setIsMarking(true)
    const targets: MarkTarget[] = [
      { entity: 'request', id: requestId, name: t('confidentiality.this-request') },
      // A supplier and its agreement often share a name, so the result message says which is which.
      ...parents
        .filter((parent) => tickedIds.has(parent.id))
        .map((parent) => ({ ...parent, name: t(`confidentiality.named-${parent.entity}`, { name: parent.name }) })),
    ]
    const { marked, failed } = await markConfidentialInOrder(targets, async (target) =>
      updateRecordConfidentiality({
        authorization: await getToken(),
        entity: target.entity,
        recordId: target.id,
        isConfidential: true,
      })
    )
    if (failed.length > 0) {
      const names = (records: MarkTarget[]) => records.map(({ name }) => name).join(', ')
      enqueueSnackbar(
        marked.length > 0
          ? t('confidentiality.mark-partly-failed', { marked: names(marked), failed: names(failed) })
          : t('confidentiality.mark-failed', { failed: names(failed) }),
        { variant: 'error' }
      )
    }
    onClose()
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [RequestDetailQueryKey, requestId] }),
      queryClient.invalidateQueries({ queryKey: [GetRequestDocumentsQueryKey, requestId] }),
      invalidateRecordAuthorization(queryClient),
    ])
  }

  return (
    <ConfirmDialog
      open
      onClose={isMarking ? () => undefined : onClose}
      onConfirm={() => void mark()}
      disableAutoCloseOnConfirm
      title={t('confidentiality.confirm-title')}
      bodyTexts={bodyTexts}
      actions={[
        {
          variant: 'contained',
          color: 'secondary',
          text: t('confidentiality.confirm-cancel'),
          action: 'cancel',
          disabled: isMarking,
        },
        { variant: 'contained', color: 'error', text: t('confidentiality.confirm-mark'), action: 'confirm', loading: isMarking },
      ]}
    >
      {parents.length > 0 && (
        <>
          <FormGroup>
            {parents.map((parent) => (
              <AlsoMarkCheckbox
                key={parent.id}
                parent={parent}
                checked={tickedIds.has(parent.id)}
                disabled={isMarking}
                onChange={(checked) => setTicked(parent.id, checked)}
              />
            ))}
          </FormGroup>
          {tickedIds.size > 0 && <Alert severity="warning">{t('confidentiality.also-mark-warning')}</Alert>}
        </>
      )}
    </ConfirmDialog>
  )
}
